// public/js/features/pois/poi-nav.js
// Collapsible "POIs" entry in the sidebar: search box, "+" add button and a
// modern checkbox list to toggle POI types on the map. Default collapsed.
import { poiState } from './poi-state.js';
import { ensurePoiData } from './poi-data.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { overlaysVisible } from './poi-overlay-layer.js';

export function initPoiNav(groups = []) {
  if (!can(groups, 'listPois')) return;
  const nav = document.getElementById('nav-menu');
  if (!nav || document.getElementById('poi-nav')) return;

  const canAdd = can(groups, 'createPoi') || can(groups, 'managePublicPois');

  const wrapper = document.createElement('div');
  wrapper.id = 'poi-nav';
  // POIs are a sub-item of Map: indented under it (`nav-sub` gives the indent +
  // the connecting line), but hidden unless the map page is active — see
  // showMapFiltersDirectlyUnderMapLink / hideMapFilters. The panel toggles map
  // LAYERS, so showing it anywhere else would offer switches that control
  // nothing.
  wrapper.className = 'nav-sub-wrap d-none';
  wrapper.innerHTML = `
    <div class="nav-link nav-sub d-flex align-items-center justify-content-between" id="poi-nav-header" style="cursor:pointer;">
      <span><i class="fa-solid fa-location-dot me-3"></i><span>POIs</span></span>
      <span class="d-flex align-items-center gap-2">
        ${canAdd ? `<span id="poi-add-btn" title="Add POI" style="cursor:pointer;"><i class="fa-solid fa-plus"></i></span>` : ''}
        <i class="fa-solid fa-chevron-down poi-chevron" style="font-size:0.7rem;transition:transform .2s;"></i>
      </span>
    </div>
    <div class="collapse" id="poi-nav-body">
      <div class="px-3 pb-2 pt-1">
        <input id="poi-search" class="form-control form-control-sm mb-2" placeholder="Search POIs...">
        <div id="poi-search-results" class="mb-2"></div>
        <div class="small text-white-50 mb-1">TYPES</div>
        <div id="poi-type-checkboxes" class="small"></div>
      </div>
    </div>`;

  // Insert right after the Map link if present, else append.
  const mapLink = nav.querySelector('a[href="#map"]');
  if (mapLink) mapLink.insertAdjacentElement('afterend', wrapper);
  else nav.appendChild(wrapper);

  const bodyEl = wrapper.querySelector('#poi-nav-body');
  const collapse = new bootstrap.Collapse(bodyEl, { toggle: false });
  const chevron = wrapper.querySelector('.poi-chevron');

  wrapper.querySelector('#poi-nav-header').addEventListener('click', (e) => {
    if (e.target.closest('#poi-add-btn')) return; // "+" handled separately
    collapse.toggle();
  });
  bodyEl.addEventListener('show.bs.collapse', () => { chevron.style.transform = 'rotate(180deg)'; });
  bodyEl.addEventListener('hide.bs.collapse', () => { chevron.style.transform = 'rotate(0deg)'; });

  // "+" → go to map and start place mode.
  wrapper.querySelector('#poi-add-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    goToMapThen(() => window.poiStartPlace?.());
  });

  // Search
  const searchInput = wrapper.querySelector('#poi-search');
  const resultsEl = wrapper.querySelector('#poi-search-results');
  let t;
  searchInput.addEventListener('input', () => {
    clearTimeout(t);
    t = setTimeout(() => renderSearchResults(searchInput.value.trim(), resultsEl), 200);
  });

  // Re-render the checkbox list and any open search results whenever data changes.
  poiState.onChange(() => {
    renderCheckboxes(wrapper.querySelector('#poi-type-checkboxes'));
    if (searchInput.value.trim()) renderSearchResults(searchInput.value.trim(), resultsEl);
  });

  // App-wide data hooks:
  //   poiEnsureData  → cache-first (used when something just needs POIs loaded)
  //   poiRefreshData → force a backend refresh + cache update (after mutations)
  // NB: we intentionally do NOT load at startup anymore — POIs load lazily on
  // the map page (cache-first), so non-map sessions never hit the backend.
  window.poiEnsureData  = (opts) => ensurePoiData(opts).catch(e => console.warn('[poi-nav] ensure failed', e));
  window.poiRefreshData = () => ensurePoiData({ force: true }).catch(e => console.warn('[poi-nav] refresh failed', e));
}

function renderCheckboxes(container) {
  if (!container) return;
  if (poiState.types.length === 0) {
    container.innerHTML = `<div class="text-white-50 fst-italic">No POI types yet.</div>`;
    return;
  }
  // Geofence circle layer toggle (persisted; layer visibility on the map).
  const geoVisible = (localStorage.getItem('poi_geofences_visible') ?? '1') === '1';
  const geofenceToggle = `
    <div class="d-flex align-items-center gap-2 py-1 border-bottom border-secondary mb-1" style="border-color:#334155!important;">
      <input class="form-check-input m-0" type="checkbox" id="poi-cb-geofences" ${geoVisible ? 'checked' : ''}>
      <label class="d-flex align-items-center gap-2 mb-0" for="poi-cb-geofences" style="cursor:pointer;">
        <span style="display:inline-flex;width:18px;height:18px;border-radius:50%;border:1.5px dashed #3b82f6;background:rgba(59,130,246,.15);"></span>
        <span>Geofences</span>
      </label>
    </div>`;
  // Custom map-overlay layer toggle (docs/poi-custom-maps.md).
  const overlaysToggle = `
    <div class="d-flex align-items-center gap-2 py-1 border-bottom border-secondary mb-1" style="border-color:#334155!important;">
      <input class="form-check-input m-0" type="checkbox" id="poi-cb-overlays" ${overlaysVisible() ? 'checked' : ''}>
      <label class="d-flex align-items-center gap-2 mb-0" for="poi-cb-overlays" style="cursor:pointer;">
        <span style="display:inline-flex;width:18px;height:18px;border-radius:4px;background:rgba(148,163,184,.25);align-items:center;justify-content:center;font-size:10px;"><i class="fa-regular fa-image"></i></span>
        <span>Map overlays</span>
      </label>
    </div>`;
  container.innerHTML = geofenceToggle + overlaysToggle + poiState.types.map(t => `
    <div class="d-flex align-items-center gap-2 py-1">
      <input class="form-check-input m-0 poi-type-cb" type="checkbox" id="poi-cb-${t.poiId}"
             data-type-id="${t.poiId}" ${poiState.enabledTypeIds.has(t.poiId) ? 'checked' : ''}>
      <label class="d-flex align-items-center gap-2 mb-0" for="poi-cb-${t.poiId}" style="cursor:pointer;">
        <span style="display:inline-flex;width:18px;height:18px;border-radius:4px;background:${t.color || '#0d6efd'};color:#fff;align-items:center;justify-content:center;font-size:10px;">
          <i class="${t.icon || 'fa-solid fa-location-dot'}"></i>
        </span>
        <span>${escapeHtml(t.name)}</span>
      </label>
    </div>`).join('');

  container.querySelectorAll('.poi-type-cb').forEach(cb => {
    cb.addEventListener('change', () => poiState.toggleType(cb.dataset.typeId, cb.checked));
  });
  container.querySelector('#poi-cb-geofences')?.addEventListener('change', (e) => {
    window.poiToggleGeofences?.(e.target.checked);
    // Persist even when the map isn't open yet (the layer reads this on init).
    localStorage.setItem('poi_geofences_visible', e.target.checked ? '1' : '0');
  });
  container.querySelector('#poi-cb-overlays')?.addEventListener('change', (e) => {
    // Persist first (the overlay layer reads this on init), then toggle live.
    localStorage.setItem('poi_overlays_visible', e.target.checked ? '1' : '0');
    window.poiToggleOverlays?.(e.target.checked);
  });
}

function renderSearchResults(term, container) {
  if (!term) { container.innerHTML = ''; return; }
  const lc = term.toLowerCase();
  const matches = poiState.pois.filter(p =>
    [p.name, p.shortName, p.description].filter(Boolean).join(' ').toLowerCase().includes(lc)
  ).slice(0, 8);

  if (matches.length === 0) {
    container.innerHTML = `<div class="text-white-50 fst-italic small">No matches</div>`;
    return;
  }
  container.innerHTML = matches.map(p => `
    <a href="#" class="d-block text-truncate text-decoration-none poi-search-hit"
       data-poi-id="${p.poiId}" style="color:#cfe2ff;padding:2px 0;">
      <i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(p.name || p.shortName || 'POI')}
    </a>`).join('');

  container.querySelectorAll('.poi-search-hit').forEach(a => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const id = a.dataset.poiId;
      goToMapThen(() => window.poiFocus?.(id, true));
    });
  });
}

// Navigate to the map page if needed, then run fn once it is ready.
function goToMapThen(fn) {
  if (window.location.hash !== '#map') {
    window.location.hash = '#map';
    setTimeout(fn, 600);
  } else {
    fn();
  }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
